import { Plus, Trash2 } from "lucide-react";
import ResumeSection from "@/components/resume/ResumeSection";

const EMPTY_EXPERIENCE = {
  company: "",
  title: "",
  start_date: "",
  end_date: "",
  location: "",
  summary: "",
  bullets: [],
};

function experienceKey(exp, idx) {
  return `exp-${idx}-${(exp.company || "").slice(0, 24)}-${(exp.title || "").slice(0, 24)}`;
}

function ExperienceEditCard({ exp, idx, onChange, onRemove }) {
  const update = (patch) => onChange({ ...exp, ...patch });
  return (
    <div className="space-y-2 p-3 border border-dashed border-[var(--border-default)]">
      <div className="grid grid-cols-2 gap-2">
        <input
          placeholder="Title"
          value={exp.title || ""}
          onChange={(e) => update({ title: e.target.value })}
          className="swiss-input !p-2"
        />
        <input
          placeholder="Company"
          value={exp.company || ""}
          onChange={(e) => update({ company: e.target.value })}
          className="swiss-input !p-2"
        />
        <input
          placeholder="Start"
          value={exp.start_date || ""}
          onChange={(e) => update({ start_date: e.target.value })}
          className="swiss-input !p-2"
        />
        <input
          placeholder="End"
          value={exp.end_date || ""}
          onChange={(e) => update({ end_date: e.target.value })}
          className="swiss-input !p-2"
        />
        <input
          placeholder="Location"
          value={exp.location || ""}
          onChange={(e) => update({ location: e.target.value })}
          className="swiss-input !p-2 col-span-2"
        />
      </div>
      <textarea
        rows={3}
        placeholder="Role summary (2-3 sentences)"
        value={exp.summary || ""}
        onChange={(e) => update({ summary: e.target.value })}
        className="swiss-input"
        data-testid={`edit-exp-summary-${idx}`}
      />
      <textarea
        rows={3}
        placeholder="One achievement per line"
        value={(exp.bullets || []).join("\n")}
        onChange={(e) =>
          update({
            bullets: e.target.value
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean),
          })
        }
        className="swiss-input"
      />
      <button
        type="button"
        onClick={onRemove}
        className="btn-ghost text-[var(--brand-accent)]"
      >
        <Trash2 className="w-3.5 h-3.5" /> REMOVE
      </button>
    </div>
  );
}

function ExperienceViewItem({ exp, idx }) {
  const dateRange = [exp.start_date, exp.end_date].filter(Boolean).join(" – ");
  return (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-heading font-semibold text-[15px] text-[var(--text-soft)]">
          {exp.title}
          {exp.title && exp.company ? " — " : ""}
          <span className="text-[var(--brand-primary)] font-medium">
            {exp.company}
          </span>
        </div>
        <div className="font-mono text-[11px] text-[var(--text-muted)] uppercase tracking-wider">
          {dateRange}
          {exp.location && ` · ${exp.location}`}
        </div>
      </div>
      {exp.summary && (
        <p
          className="mt-2 text-[14px] leading-relaxed text-[var(--text-soft)]"
          data-testid={`experience-summary-${idx}`}
        >
          {exp.summary}
        </p>
      )}
      {(exp.bullets || []).length > 0 && (
        <ul className="mt-2 space-y-1.5 text-[13.5px] leading-relaxed text-[var(--text-secondary)]">
          {exp.bullets.map((b, j) => (
            <li
              key={`b-${idx}-${j}-${b.slice(0, 24)}`}
              className="flex gap-3 before:content-['—'] before:text-[var(--text-muted)]"
            >
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function ExperienceSection({
  experience = [],
  editing,
  onAdd,
  onUpdate,
  onRemove,
}) {
  return (
    <ResumeSection
      title="Experience"
      index={3}
      action={
        editing && (
          <button
            type="button"
            onClick={() => onAdd(EMPTY_EXPERIENCE)}
            className="btn-ghost"
            data-testid="add-experience-button"
          >
            <Plus className="w-3.5 h-3.5" /> ADD
          </button>
        )
      }
    >
      <div className="space-y-6">
        {experience.map((exp, idx) => (
          <div
            key={experienceKey(exp, idx)}
            data-testid={`experience-item-${idx}`}
          >
            {editing ? (
              <ExperienceEditCard
                exp={exp}
                idx={idx}
                onChange={(next) => onUpdate(idx, next)}
                onRemove={() => onRemove(idx)}
              />
            ) : (
              <ExperienceViewItem exp={exp} idx={idx} />
            )}
          </div>
        ))}
      </div>
    </ResumeSection>
  );
}
